import React, { Fragment, useContext, useState, useEffect } from "react";
import Icofont from "react-icofont";

import "../Assets/Css/body.css";
import "../Assets/Css/heading.css";
import AppContext from "./AppContext";
import { WelcomeOrderText, ProStrings, ClubStrings } from "./Texts.js";

const RECAPTCHA_SITE_KEY = "6LcqGccsAAAAADSvNFa8uK47ndnCAHmvHynfesl5";
const RECAPTCHA_ENABLED = RECAPTCHA_SITE_KEY !== "RECAPTCHA_SITE_KEY";

const PICKUP_LOCATIONS = ["Horgen", "Luzern (LIDO)", "Richterswil", "Wädenswil", "Zurich Enge"];
const TENSIONS = Array.from({ length: 12 }, (_, i) => i + 18); // 18 – 29 kg

const UI = {
	de: {
		name: "Vorname und Name",
		email: "E-Mail",
		phone: "Telefon (optional)",
		rackets: "Anzahl Schläger",
		string: "Saite",
		mains: "Bespannungshärte Längssaiten",
		cross: "Bespannungshärte Quersaiten",
		location: "Abholungsort",
		remarks: "Bemerkungen (optional)",
		submit: "Bestellung absenden",
		sending: "Wird gesendet…",
		proGroup: "Pro Besaitung",
		clubGroup: "Einzelsaiten",
		success: "Ihre Bestellung wurde erfolgreich übermittelt! Wir werden Sie in Kürze kontaktieren.",
		error: "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
		selectDefault: "Bitte auswählen…",
		invalidField: "Bitte füllen Sie dieses Feld aus.",
		invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
		recaptchaNote: "Diese Website ist durch reCAPTCHA geschützt.",
	},
	en: {
		name: "First and Last Name",
		email: "Email",
		phone: "Phone (optional)",
		rackets: "Number of Rackets",
		string: "String",
		mains: "Main String Tension",
		cross: "Cross String Tension",
		location: "Pickup Location",
		remarks: "Remarks (optional)",
		submit: "Submit Order",
		sending: "Sending…",
		proGroup: "Pro Stringing",
		clubGroup: "Selected Strings",
		success: "Your order was submitted successfully! We will contact you shortly.",
		error: "An error occurred. Please try again.",
		selectDefault: "Please select…",
		invalidField: "Please fill in this field.",
		invalidEmail: "Please enter a valid email address.",
		recaptchaNote: "This site is protected by reCAPTCHA.",
	},
};

const EMPTY = {
	name: "", email: "", phone: "",
	rackets: "", string: "", mains: "", cross: "",
	location: "", remarks: "", honeypot: "",
};

export const OrderForm = () => {
	const context = useContext(AppContext);
	const isDE = context.lang.get === "en"; // "en" state = German page (per app convention)
	const t = isDE ? UI.de : UI.en;

	const [form, setForm]     = useState(EMPTY);
	const [errors, setErrors] = useState({});
	const [status, setStatus] = useState(null); // null | "sending" | "success" | "error"

	useEffect(() => {
		const val = context.selectedString?.get;
		if (val) {
			setForm(f => ({ ...f, string: val }));
			setErrors(e => ({ ...e, string: undefined }));
			context.selectedString.set("");
		}
	}, [context.selectedString?.get]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!RECAPTCHA_ENABLED) return;
		if (document.getElementById("recaptcha-script")) return;
		const s = document.createElement("script");
		s.id  = "recaptcha-script";
		s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
		s.async = true;
		document.head.appendChild(s);
	}, []);

	const validate = () => {
		const e = {};
		if (!form.name.trim())    e.name     = t.invalidField;
		if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
		                          e.email    = t.invalidEmail;
		if (!form.rackets)        e.rackets  = t.invalidField;
		if (!form.string)         e.string   = t.invalidField;
		if (!form.mains)          e.mains    = t.invalidField;
		if (!form.cross)          e.cross    = t.invalidField;
		if (!form.location)       e.location = t.invalidField;
		return e;
	};

	const handleChange = ({ target: { name, value } }) => {
		setForm(f => ({ ...f, [name]: value }));
		if (errors[name]) setErrors(e => ({ ...e, [name]: undefined }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const errs = validate();
		if (Object.keys(errs).length > 0) { setErrors(errs); return; }

		setStatus("sending");
		try {
			let token = "";
			if (RECAPTCHA_ENABLED && window.grecaptcha) {
				token = await new Promise(resolve =>
					window.grecaptcha.ready(() =>
						window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: "submit" }).then(resolve)
					)
				);
			}

			const res  = await fetch("/contact.php", {
				method:  "POST",
				headers: { "Content-Type": "application/json" },
				body:    JSON.stringify({ ...form, recaptcha: token }),
			});
			const data = await res.json();
			if (data.success) {
				setStatus("success");
				setForm(EMPTY);
			} else {
				setStatus("error");
			}
		} catch {
			setStatus("error");
		}
	};

	return (
		<Fragment>
			<section className="page-section portfolio mb-0" id="neworder" style={{ backgroundColor: "#f0f4f9" }}>
				<div className="container">
					<div className="text-center">
						<h2 className="page-section-heading text-secondary mb-3 d-inline-block">
							{context.lang.get === "en" ? WelcomeOrderText[0].title_DE : WelcomeOrderText[0].title_ENG}
						</h2>
					</div>
					<div className="divider-custom">
						<div className="divider-custom-line"></div>
						<div className="divider-custom-icon">
							<Icofont icon="icofont-tennis" />
						</div>
						<div className="divider-custom-line"></div>
					</div>

					<div className="row justify-content-center">
						<div className="col-lg-8">

							{status === "success" && (
								<div className="alert alert-success text-center" role="alert">
									{t.success}
								</div>
							)}
							{status === "error" && (
								<div className="alert alert-danger text-center" role="alert">
									{t.error}
								</div>
							)}

							{status !== "success" && (
								<form onSubmit={handleSubmit} noValidate>
									{/* Honeypot — invisible to humans */}
									<input
										type="text"
										name="honeypot"
										value={form.honeypot}
										onChange={handleChange}
										style={{ display: "none" }}
										tabIndex="-1"
										autoComplete="off"
									/>

									<div className="row g-3">
										{/* Name */}
										<div className="col-sm-6">
											<label className="form-label fw-semibold">{t.name} *</label>
											<input
												type="text"
												className={`form-control ${errors.name ? "is-invalid" : ""}`}
												name="name"
												value={form.name}
												onChange={handleChange}
												maxLength={100}
											/>
											{errors.name && <div className="invalid-feedback">{errors.name}</div>}
										</div>

										{/* Email */}
										<div className="col-sm-6">
											<label className="form-label fw-semibold">{t.email} *</label>
											<input
												type="email"
												className={`form-control ${errors.email ? "is-invalid" : ""}`}
												name="email"
												value={form.email}
												onChange={handleChange}
												maxLength={100}
											/>
											{errors.email && <div className="invalid-feedback">{errors.email}</div>}
										</div>

										{/* Phone */}
										<div className="col-sm-6">
											<label className="form-label fw-semibold">{t.phone}</label>
											<input
												type="tel"
												className="form-control"
												name="phone"
												value={form.phone}
												onChange={handleChange}
												maxLength={30}
											/>
										</div>

										{/* Rackets */}
										<div className="col-sm-6">
											<label className="form-label fw-semibold">{t.rackets} *</label>
											<select
												className={`form-select ${errors.rackets ? "is-invalid" : ""}`}
												name="rackets"
												value={form.rackets}
												onChange={handleChange}
											>
												<option value="">{t.selectDefault}</option>
												{[1,2,3,4,5,6,7,8].map(n => (
													<option key={n} value={n}>{n}</option>
												))}
											</select>
											{errors.rackets && <div className="invalid-feedback">{errors.rackets}</div>}
										</div>

										{/* String — grouped by section */}
										<div className="col-12">
											<label className="form-label fw-semibold">{t.string} *</label>
											<select
												className={`form-select ${errors.string ? "is-invalid" : ""}`}
												name="string"
												value={form.string}
												onChange={handleChange}
											>
												<option value="">{t.selectDefault}</option>
												<optgroup label={t.proGroup}>
													{ProStrings.map((s, i) => (
														<option key={i} value={s.string_DE}>
															{isDE ? s.string_DE : s.string_ENG} — CHF {s.price}
														</option>
													))}
												</optgroup>
												<optgroup label={t.clubGroup}>
													{ClubStrings.map((s, i) => (
														<option key={i} value={s.string_DE}>
															{isDE ? s.string_DE : s.string_ENG} — CHF {s.price}
														</option>
													))}
												</optgroup>
											</select>
											{errors.string && <div className="invalid-feedback">{errors.string}</div>}
										</div>

										{/* Tensions */}
										<div className="col-sm-6">
											<label className="form-label fw-semibold">{t.mains} *</label>
											<select
												className={`form-select ${errors.mains ? "is-invalid" : ""}`}
												name="mains"
												value={form.mains}
												onChange={handleChange}
											>
												<option value="">{t.selectDefault}</option>
												{TENSIONS.map(v => (
													<option key={v} value={v}>{v} kg</option>
												))}
											</select>
											{errors.mains && <div className="invalid-feedback">{errors.mains}</div>}
										</div>

										<div className="col-sm-6">
											<label className="form-label fw-semibold">{t.cross} *</label>
											<select
												className={`form-select ${errors.cross ? "is-invalid" : ""}`}
												name="cross"
												value={form.cross}
												onChange={handleChange}
											>
												<option value="">{t.selectDefault}</option>
												{TENSIONS.map(v => (
													<option key={v} value={v}>{v} kg</option>
												))}
											</select>
											{errors.cross && <div className="invalid-feedback">{errors.cross}</div>}
										</div>

										{/* Pickup location */}
										<div className="col-12">
											<label className="form-label fw-semibold">{t.location} *</label>
											<select
												className={`form-select ${errors.location ? "is-invalid" : ""}`}
												name="location"
												value={form.location}
												onChange={handleChange}
											>
												<option value="">{t.selectDefault}</option>
												{PICKUP_LOCATIONS.map(loc => (
													<option key={loc} value={loc}>{loc}</option>
												))}
											</select>
											{errors.location && <div className="invalid-feedback">{errors.location}</div>}
										</div>

										{/* Remarks */}
										<div className="col-12">
											<label className="form-label fw-semibold">{t.remarks}</label>
											<textarea
												className="form-control"
												name="remarks"
												value={form.remarks}
												onChange={handleChange}
												rows={4}
												maxLength={1000}
											/>
										</div>

										{/* Submit */}
										<div className="col-12 text-center mt-2">
											<button
												type="submit"
												className="btn btn-primary btn-lg px-5"
												disabled={status === "sending"}
											>
												{status === "sending" ? t.sending : t.submit}
											</button>
											{RECAPTCHA_ENABLED && (
												<p className="text-muted small mt-2 mb-0">
													{t.recaptchaNote}{" "}
													<a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
													{" & "}
													<a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a>
												</p>
											)}
										</div>
									</div>
								</form>
							)}
						</div>
					</div>
				</div>
			</section>
		</Fragment>
	);
};

export default OrderForm;
